// Bass clef sailing like a ship on the sea — the OSRR brand metaphor.
// Pure SVG + CSS so it ships crisp at any resolution and never blocks the LCP.

export default function BassClefShip({ className = "" }) {
  return (
    <div
      className={`bass-clef-ship relative w-full h-full pointer-events-none ${className}`}
      aria-hidden="true"
      data-testid="bass-clef-ship"
    >
      {/* Faint star/dust layer */}
      <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice">
        <defs>
          <radialGradient id="bcs-glow" cx="50%" cy="42%" r="35%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.18" />
            <stop offset="60%" stopColor="#7DA3D9" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#0A1128" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="800" height="600" fill="url(#bcs-glow)" />
        {/* sparse stars */}
        {[
          [60, 80], [140, 120], [220, 60], [320, 110], [420, 50], [540, 90],
          [620, 140], [720, 70], [80, 220], [200, 250], [380, 200], [560, 280],
          [680, 220], [120, 360], [300, 340], [500, 380], [660, 360],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 4 === 0 ? 1.4 : 0.8} fill="#B8D0EE" opacity={0.55} />
        ))}
      </svg>

      {/* Bass clef "ship" — drawn as SVG so it can carry the gold gradient & gentle rocking */}
      <svg
        className="absolute left-1/2 top-[18%] -translate-x-1/2 w-[60%] max-w-[420px] bcs-rock"
        viewBox="0 0 240 360"
        fill="none"
      >
        <defs>
          <linearGradient id="bcs-gold" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F4D77A" />
            <stop offset="55%" stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#8C7222" />
          </linearGradient>
          <filter id="bcs-soft" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.4" />
          </filter>
        </defs>

        {/* Soft drop-glow */}
        <g filter="url(#bcs-soft)" opacity="0.55">
          <path
            d="M70 60 C 150 60, 200 130, 170 210 C 145 275, 80 290, 60 250"
            stroke="#D4AF37"
            strokeWidth="34"
            strokeLinecap="round"
            fill="none"
          />
        </g>

        {/* Main bass-clef stroke (the F-clef curl) */}
        <path
          d="M70 60 C 150 60, 200 130, 170 210 C 145 275, 80 290, 60 250"
          stroke="url(#bcs-gold)"
          strokeWidth="22"
          strokeLinecap="round"
          fill="none"
        />

        {/* The two dots of the bass clef — sitting like signal lamps on the deck */}
        <circle cx="200" cy="120" r="11" fill="url(#bcs-gold)" />
        <circle cx="200" cy="170" r="11" fill="url(#bcs-gold)" />

        {/* Inner highlight */}
        <path
          d="M76 72 C 142 72, 188 134, 162 205"
          stroke="#FBE8A6"
          strokeWidth="4"
          strokeLinecap="round"
          fill="none"
          opacity="0.7"
        />

        {/* "Mast" — a subtle vertical line suggesting a ship's mast through the clef */}
        <line x1="120" y1="48" x2="120" y2="282" stroke="#FBE8A6" strokeWidth="1.5" opacity="0.35" strokeDasharray="2 6" />

        {/* Reflection on water */}
        <g transform="translate(0, 320) scale(1, -0.35)" opacity="0.35">
          <path
            d="M70 60 C 150 60, 200 130, 170 210 C 145 275, 80 290, 60 250"
            stroke="url(#bcs-gold)"
            strokeWidth="18"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      </svg>

      {/* Layered animated waves */}
      <svg
        className="absolute left-0 right-0 bottom-0 w-full h-[48%]"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="wave-deep" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0F1E40" stopOpacity="0" />
            <stop offset="100%" stopColor="#0F1E40" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="wave-mid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1A2F5C" stopOpacity="0" />
            <stop offset="100%" stopColor="#1A2F5C" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="wave-top" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7DA3D9" stopOpacity="0" />
            <stop offset="100%" stopColor="#7DA3D9" stopOpacity="0.35" />
          </linearGradient>
        </defs>

        {/* deep wave */}
        <path className="bcs-wave bcs-wave-deep" fill="url(#wave-deep)"
          d="M0,180 C200,140 360,220 540,180 C720,140 900,220 1080,180 C1260,140 1380,200 1440,180 L1440,320 L0,320 Z" />
        {/* mid wave */}
        <path className="bcs-wave bcs-wave-mid" fill="url(#wave-mid)"
          d="M0,210 C220,170 380,250 580,210 C760,180 940,250 1120,210 C1280,180 1380,230 1440,210 L1440,320 L0,320 Z" />
        {/* top crest wave */}
        <path className="bcs-wave bcs-wave-top" fill="url(#wave-top)"
          d="M0,240 C240,210 400,270 600,240 C780,215 960,265 1140,240 C1300,220 1380,250 1440,240 L1440,320 L0,320 Z" />
      </svg>
    </div>
  );
}
